import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectsByCountry } from '../../../redux/actions/indicatorProjects';
import { BarChart } from '../../Portfolio/CardIndicator/Cards';
import { HeaderData } from './HeaderData/HeaderData';
import styles from './ProjectsByCountry.module.scss';
import { Table } from './Table/Table';
import { handleDataInDetail } from './Util';
import Country from '../../../models/Country';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import Filter from '../../Portfolio/Filter/Filter';
import SpinningLoader from '../../ProjectDetail/ProjectTabs/Loader/SpinningLoader';
import { ProjectsListSkeleton } from '../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';

const handleDynamicChartDimensions = data => {
  const quantity = data && data.subLabels && data.subLabels.length;
  return {
    mobile: { width: 320, height: quantity ? quantity * 20 : 320 },
    desktop: { width: 320, height: quantity && quantity > 10 ? quantity * 15 : 550, cursor: 'pointer' },
  };
};

const getCountryBy = (regions, id) => {
  for (const region of regions) {
    const countryAux = region.countries.find(cy => cy.id === parseInt(id));
    if (countryAux) {
      return countryAux;
    }
  }
  return { code: 'REG', id: parseInt(id), name: 'regional', regionId: 0 };
};

export const ProjectsByCountry = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentIndicator, regions } = useSelector<any, any>(state => state);
  const { type, data, detailTitle, detailChartFoot, totalProjects } = currentIndicator;
  const COUNTRY_INDICATOR_CODE =
    sessionStorage.getItem('COUNTRY_INDICATOR_CODE') || (data && data.completeGroupingValue[0].trim());

  const [selectedCountry, setSelectedCountry] = useState<Partial<Country>>({});
  const qtyCountries = data && data.labels && data.labels.length;

  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

  const {
    chartDetailContainer,
    capitalizeWord,
    subTitle,
    chartSection,
    projectsByCountryContainer,
    chartContainer,
    tableContainer,
    title,
    footerTitle,
    footerSubTitle,
    tableSection,
    tableDetail,
    titleUpper,
    description,
    left,
    right,
  } = styles;

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjectsData, setTotalProjectsData] = React.useState(false);
  const [flag, setFlag] = useState<boolean>(false);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjectsData(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(
      setCurrentIndicator(
        sendEndSummary?.find(data => data.indicatorKey === 'PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_COUNTRY'),
      ),
    );
    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    setIsLoadingData(true);
    const firstCountry = getCountryBy(regions, COUNTRY_INDICATOR_CODE);
    setSelectedCountry(firstCountry);
    dispatch(getProjectsByCountry(COUNTRY_INDICATOR_CODE, projectTypeFilterChange));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndicator]);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    setFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0 &&
      flag
    ) {
      handleCommonFilterChange();
      setFlag(false);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  const datasetClick = value => {
    if (isLoadingData) {
      return;
    }
    setIsLoadingData(true);
    sessionStorage.removeItem('PaginationIndicator');
    sessionStorage.setItem('COUNTRY_INDICATOR_CODE', value.completeGroupingValue);
    const countrySelected = getCountryBy(regions, value.completeGroupingValue);
    setSelectedCountry(countrySelected);
    dispatch(getProjectsByCountry(value.completeGroupingValue));
  };
  return (
    <>
      <div className={projectsByCountryContainer}>
        <div className={chartSection}>
          <div className={chartDetailContainer}>
            <div className={title}>{t(detailTitle)}</div>
            <div className={subTitle}>
              <span className={capitalizeWord}> {t('total')}</span>: {` ${qtyCountries ?? 0} `}
              {t('countries')}
            </div>
            <div className={chartContainer}>
              {!isLoading ? (
                <BarChart
                  type={type}
                  data={handleDataInDetail(currentIndicator)}
                  showSubLabels={false}
                  listPage={true}
                  datasetClick={datasetClick}
                  chartDimensions={handleDynamicChartDimensions(data)}
                />
              ) : (
                <SpinningLoader customStyle={{ marginBottom: 85 }} />
              )}
            </div>
            <div className={footerTitle}>{t(detailChartFoot)}</div>
            <div className={footerSubTitle}>
              <span className={capitalizeWord}> {t('total')}</span>: {totalProjects ?? 0} {t('projects')}
            </div>
          </div>
        </div>
        <div className={tableSection}>
          <div className={tableDetail}>
            <div className={titleUpper}>{t('country')}</div>
            <div className={description}>
              <div className={left}>{selectedCountry.name}</div>
              <div className={right}>
                <HeaderData country={selectedCountry} />
              </div>
            </div>
            <div className={tableContainer}>
              {!isLoading ? (
                <Table hideLoader={() => setIsLoadingData(false)} isLoading={isLoading} />
              ) : (
                <ProjectsListSkeleton />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
